import React, { useContext, createContext, useState, useCallback, useMemo } from 'react'

import { BaseRbacRole, User, ZeusRbacRole } from 'api/types/userApiTypes'

import { useOrg } from './OrgContext'

export type UserContextUser = User & { isSuperAdmin: boolean; isZeusITAdmin: boolean }

export const UserContext = createContext<{
  user?: UserContextUser
  loadUserWithFetcher: (fetcher: () => Promise<User | undefined>) => void
  loading: boolean
  hasPermission: (permission: string) => boolean
}>({
  user: undefined,
  loadUserWithFetcher: () => false,
  loading: true,
  hasPermission: () => false,
})

export function useUser() {
  return useContext(UserContext)
}

export const ADMIN_ORG = import.meta.env.VITE_AUTH_ADMIN_ORG

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const { org, setOrg } = useOrg()
  const [user, setUser] = useState<UserContextUser>()
  const [loading, setLoading] = useState(true)

  const loadUserWithFetcher = useCallback(
    (fetcher: () => Promise<User | undefined>) => {
      void fetcher()
        .then((curr?: User) => {
          if (curr) {
            const userWithAdmin = {
              ...curr,
              isSuperAdmin:
                !!(ADMIN_ORG && curr.org === ADMIN_ORG) || curr.role === BaseRbacRole.SuperAdmin,
              isZeusITAdmin: curr.role === ZeusRbacRole.ZeusITAdmin,
            }

            /**
             * If we don't have an org saved in local storage,
             * fall back on the original org this user was created in.
             */
            if (!org) {
              setOrg(curr.org)
            }

            setUser(userWithAdmin)
          }
        })
        .finally(() => {
          setLoading(false)
        })
    },
    [org, setOrg]
  )

  /**
   * Returns true when the user is a SuperAdmin or if the user has the necessary permission.
   */
  const hasPermission = useCallback(
    (permission: string) => {
      if (!user) {
        return false
      }

      return user.permissions.includes(permission) || user.isSuperAdmin
    },
    [user]
  )

  const value = useMemo(
    () => ({
      user,
      loadUserWithFetcher,
      loading,
      hasPermission,
    }),
    [loadUserWithFetcher, loading, user, hasPermission]
  )

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}
